import { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import PropTypes from 'prop-types';
import { Badge } from './components/ui/badge';
import Button from './components/ui/button';
import { Card } from './components/ui/card';
import { CheckCircle, TrendingUp, Zap, Globe, Shield, Menu, Mail, Newspaper, Laptop } from 'lucide-react';

import AuthModal from './AuthModal';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

function AnimatedSection({ children }) {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (inView) {
      controls.start('animate');
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      initial="initial"
      animate={controls}
      variants={stagger}
    >
      {children}
    </motion.div>
  );
}

AnimatedSection.propTypes = {
  children: PropTypes.node.isRequired,
};

export default function LandingPage() {
  const [activeSection, setActiveSection] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signin');

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const openAuthModal = (mode) => {
    setAuthMode(mode);
    setIsAuthModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="relative z-10 px-4 py-6 md:px-6 md:py-8">
        <header className="flex justify-between items-center mb-6">
          <motion.h1 
            className="text-2xl md:text-3xl font-bold"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            News Nine
          </motion.h1>
          <nav className="hidden md:block">
            <motion.div
              variants={stagger}
              initial="initial"
              animate="animate"
              className="flex items-center space-x-4"
            >
              {['home', 'about', 'features', 'contact'].map((section) => (
                <motion.span key={section} variants={fadeInUp}>
                  <Button 
                    variant="ghost" 
                    onClick={() => setActiveSection(section)}
                    className={`text-white hover:text-yellow-300 ${activeSection === section ? 'text-yellow-300' : ''}`}
                  >
                    {section.charAt(0).toUpperCase() + section.slice(1)}
                  </Button>
                </motion.span>
              ))}
              <motion.span variants={fadeInUp}>
                <Button 
                  variant="ghost" 
                  onClick={() => openAuthModal('signin')}
                  className="text-white hover:text-yellow-300"
                >
                  Login
                </Button>
              </motion.span>
              <motion.span variants={fadeInUp}>
                
              </motion.span>
            </motion.div>
          </nav>
          <button className="md:hidden text-white" onClick={toggleMenu}>
            <Menu size={24} />
          </button>
        </header>

        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden bg-white/10 backdrop-blur-md p-4 absolute top-16 left-0 right-0 z-50"
          >
            {['home', 'about', 'features', 'contact'].map((section) => (
              <Button 
                key={section}
                variant="ghost" 
                onClick={() => {
                  setActiveSection(section);
                  setIsMenuOpen(false);
                }}
                className={`block w-full text-left text-white hover:text-yellow-300 ${activeSection === section ? 'text-yellow-300' : ''} mb-2`}
              >
                {section.charAt(0).toUpperCase() + section.slice(1)}
              </Button>
            ))}
            <Button 
              variant="ghost" 
              onClick={() => {
                openAuthModal('signin');
                setIsMenuOpen(false);
              }}
              className="block w-full text-left text-white hover:text-yellow-300 mb-2"
            >
              Login
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                openAuthModal('signup');
                setIsMenuOpen(false);
              }}
              className="w-full bg-yellow-400 text-gray-900 hover:bg-yellow-300 mt-4"
            >
              Sign Up
            </Button>
          </motion.div>
        )}

        <main className="container mx-auto">
          {activeSection === 'home' && (
            <>
              <AnimatedSection>
                <div className="py-20 md:py-32 text-center">
                  <motion.h2 
                    className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight"
                    variants={fadeInUp}
                  >
                    Don&apos;t Miss a Beat in Today&apos;s Fast-Paced World
                  </motion.h2>
                  <motion.p 
                    className="text-xl md:text-2xl mb-8 max-w-2xl mx-auto"
                    variants={fadeInUp}
                  >
                    Stay ahead with News Nine - Your gateway to unbiased, real-time news that matters.
                  </motion.p>
                  <motion.div variants={fadeInUp}>
                    <Button 
                      variant="outline"
                      onClick={() => openAuthModal('signup')}
                      className="bg-yellow-400 text-gray-900 hover:bg-yellow-300 text-lg px-8 py-3"
                    >
                      Get Started
                    </Button>
                  </motion.div>
                </div>
              </AnimatedSection>

              

              <AnimatedSection>
                <div className="py-20 md:py-32">
                  <motion.h3 
                    className="text-3xl md:text-4xl font-bold mb-12 text-center"
                    variants={fadeInUp}
                  >
                    Why Choose News Nine?
                  </motion.h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-5xl mx-auto">
                    <FeatureCard
                      icon={<CheckCircle className="h-12 w-12 mb-6 text-yellow-400" />}
                      title="Unbiased Reporting"
                      description="Our AI-driven platform ensures neutral, fact-based news coverage."
                    />
                    <FeatureCard
                      icon={<TrendingUp className="h-12 w-12 mb-6 text-yellow-400" />}
                      title="Real-time Updates"
                      description="Stay on top of breaking news with instant notifications."
                    />
                    <FeatureCard
                      icon={<Zap className="h-12 w-12 mb-6 text-yellow-400" />}
                      title="Concise Summaries"
                      description="Get the essence of complex stories in easy-to-digest formats."
                    />
                  </div>
                </div>
              </AnimatedSection>

              <AnimatedSection>
                <motion.div 
                  className="py-20 md:py-32 bg-gray-800 rounded-3xl"
                  variants={fadeInUp}
                >
                  <div className="max-w-5xl mx-auto px-6">
                    <h3 className="text-3xl md:text-4xl font-bold mb-12 text-center">How It Works</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                      <ProcessCard
                        icon={<Laptop className="h-12 w-12 mb-6 text-yellow-400" />}
                        title="Create Your Account"
                        description="Sign up for free and unlock a world of curated news."
                        step={1}
                      />
                      <ProcessCard
                        icon={<Newspaper className="h-12 w-12 mb-6 text-yellow-400" />}
                        title="Explore Topics"
                        description="Discover a wide range of news categories and interests."
                        step={2}
                      />
                      <ProcessCard
                        icon={<Zap className="h-12 w-12 mb-6 text-yellow-400" />}
                        title="Stay Informed"
                        description="Access cutting-edge news analysis and real-time updates."
                        step={3}
                      />
                    </div>
                  </div>
                </motion.div>
              </AnimatedSection>

              <AnimatedSection>
                <div className="py-20 md:py-32">
                  <motion.h3 
                    className="text-3xl md:text-4xl font-bold mb-12 text-center"
                    variants={fadeInUp}
                  >
                    Trusted by Millions Worldwide
                  </motion.h3>
                  <motion.div 
                    className="flex flex-wrap justify-center items-center gap-8"
                    variants={fadeInUp}
                  >
                    <Badge variant="secondary" className="text-lg py-2 px-4 bg-white/10">
                      <Globe className="mr-2 h-5 w-5 text-yellow-400" />
                      3+ Continents
                    </Badge>
                    <Badge variant="secondary" className="text-lg py-2 px-4 bg-white/10">
                      <Shield className="mr-2 h-5 w-5 text-yellow-400" />
                      100% Fact-Checked
                    </Badge>
                    <Badge variant="secondary" className="text-lg py-2 px-4 bg-white/10">
                      <TrendingUp className="mr-2 h-5 w-5 text-yellow-400" />
                      100+ Readers
                    </Badge>
                  </motion.div>
                </div>
              </AnimatedSection>

              <AnimatedSection>
                <div className="py-20 md:py-32 text-center">
                  <motion.h3 
                    className="text-3xl md:text-4xl font-bold mb-8"
                    variants={fadeInUp}
                  >
                    Ready to Transform Your News Experience?
                  </motion.h3>
                  <motion.p 
                    className="text-xl mb-12 max-w-2xl mx-auto"
                    variants={fadeInUp}
                  >
                    Join News Nine today and be part of the future of news consumption.
                  </motion.p>
                  <motion.div variants={fadeInUp}>
                    <Button 
                      variant="outline"
                      onClick={() => openAuthModal('signup')}
                      className="bg-yellow-400 text-gray-900 hover:bg-yellow-300 text-lg px-8 py-3"
                    >
                      Sign Up Now
                    </Button>
                  </motion.div>
                </div>
              </AnimatedSection>
            </>
          )}

          {activeSection === 'about' && (
            <AnimatedSection>
              <div className="max-w-4xl mx-auto py-20 md:py-32">
                <motion.h2 
                  className="text-3xl md:text-5xl font-bold mb-8 text-center"
                  variants={fadeInUp}
                >
                  About News Nine
                </motion.h2>
                <motion.p 
                  className="text-lg mb-8"
                  variants={fadeInUp}
                >
                  News Nine is a cutting-edge news platform dedicated to delivering accurate, unbiased, and timely information to our global audience. Founded in 2024, weve quickly become a trusted source for readers across the planet.
                </motion.p>
                <motion.p 
                  className="text-lg mb-8"
                  variants={fadeInUp}
                >
                  Our team works tirelessly to bring you the most relevant and impactful stories from around the world using our AI-powered algorithms. We believe in the power of informed citizens and strive to empower our readers with knowledge that matters.
                </motion.p>
                <motion.h3 
                  className="text-2xl font-semibold mb-4"
                  variants={fadeInUp}
                >
                  Our Mission
                </motion.h3>
                <motion.p 
                  className="text-lg mb-8"
                  variants={fadeInUp}
                >
                  To provide accessible, accurate, and unbiased news coverage that allows readers to understand the worlds current events and stay up to date.
                </motion.p>
                
              </div>
            </AnimatedSection>
          )}

          {activeSection === 'features' && (
            <AnimatedSection>
              <div className="max-w-5xl mx-auto py-20 md:py-32">
                <motion.h2 
                  className="text-3xl md:text-5xl font-bold mb-12 text-center"
                  variants={fadeInUp}
                >
                  Features
                </motion.h2>
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-2 gap-12"
                  variants={stagger}
                >
                  <FeatureCard
                    icon={<Globe className="h-12 w-12 mb-6 text-yellow-400" />}
                    title="Global Coverage"
                    description="News from every corner of the world, keeping you informed about international events."
                  />
                  <FeatureCard
                    icon={<Newspaper className="h-12 w-12 mb-6 text-yellow-400" />}
                    title="Unbiased News"
                    description="Our advanced algorithms ensure that there are no biases in any of the articles displayed."
                  />
                  <FeatureCard
                    icon={<CheckCircle className="h-12 w-12 mb-6 text-yellow-400" />}
                    title="Fact-Checking"
                    description="Our rigorous fact-checking process ensures the highest level of accuracy in our reporting."
                  />
                  <FeatureCard
                    icon={<TrendingUp className="h-12 w-12 mb-6 text-yellow-400" />}
                    title="Trending Topics"
                    description="Stay on top of what's buzzing with our real-time trending stories section."
                  />
                </motion.div>
                
              </div>
            </AnimatedSection>
          )}

          {activeSection === 'contact' && (
            <AnimatedSection>
              <div className="max-w-4xl mx-auto py-20 md:py-32">
                <motion.h2 
                  className="text-3xl md:text-5xl font-bold mb-8 text-center"
                  variants={fadeInUp}
                >
                  Contact Us
                </motion.h2>
                <motion.p 
                  className="text-xl mb-12 text-center"
                  variants={fadeInUp}
                >
                  Were always here to help. Reach out to us through our email:
                </motion.p>
                <motion.div 
                  className="flex justify-center"
                  variants={stagger}
                >
                  <div className="w-full max-w-md">
                    <ContactCard
                      icon={<Mail className="h-12 w-12 mb-6 text-yellow-400" />}
                      title="Email"
                      info="n3wsn1n3@gmail.com"
                    />
                  </div>
                </motion.div>
              </div>
            </AnimatedSection>
          )}
        </main>

        <footer className="mt-20 text-center py-8">
          <p className="text-sm md:text-base">&copy; 2024 News Nine. All rights reserved.</p>
        </footer>
      </div>

      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
        initialMode={authMode} 
      />
    </div>
  );
}

function FeatureCard({ icon, title, description }) {
  return (
    <motion.div variants={fadeInUp}>
      <Card className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors duration-300 h-full">
        <div className="p-8 text-center h-full flex flex-col justify-between">
          {icon}
          <h3 className="text-2xl font-semibold mb-4">{title}</h3>
          <p className="text-white/80">{description}</p>
        </div>
      </Card>
    </motion.div>
  );
}

FeatureCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

function ContactCard({ icon, title, info }) {
  return (
    <motion.div variants={fadeInUp}>
      <Card className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors duration-300 h-full">
        <div className="p-8 text-center h-full flex flex-col items-center justify-center">
          {icon}
          <h3 className="text-2xl font-semibold mb-4">{title}</h3>
          <p className="text-white/80 text-lg">{info}</p>
        </div>
      </Card>
    </motion.div>
  );
}

ContactCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  info: PropTypes.string.isRequired,
};

function ProcessCard({ icon, title, description, step }) {
  return (
    <motion.div variants={fadeInUp}>
      <Card className="bg-white/5 border-white/10 hover:bg-white/10 transition-colors duration-300 h-full relative">
        <div className="absolute -top-4 -left-4 bg-yellow-400 text-gray-900 rounded-full w-10 h-10 flex items-center justify-center font-bold text-xl">
          {step}
        </div>
        <div className="p-8 text-center h-full flex flex-col justify-between">
          {icon}
          <h3 className="text-2xl font-semibold mb-4">{title}</h3>
          <p className="text-white/80">{description}</p>
        </div>
      </Card>
    </motion.div>
  );
}

ProcessCard.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  step: PropTypes.number.isRequired,
};